import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import SignaturePad from './SignaturePad';
import { useInlineDocument } from '../hooks/useSigningScroll';
import { renderPdfPages, renderRatio } from '../constants/documentViewerCore';
import { loadPdfJs } from '../utils/pdfjsLoader';
import useBodyScrollLock from '../hooks/useBodyScrollLock';

/**
 * In-app canvas signing for the B-C Purchase and Sale Agreement.
 *
 * Each user (primary borrower or co-borrower) signs in their OWN session.
 * The server determines which slot to stamp based on the authenticated
 * user — the client never sends or trusts a signer_role field. Jesse's
 * countersign envelope fires automatically only once all required
 * borrowers have signed.
 *
 * Four phases, surfaced as a single modal:
 *   0. Consent — ESIGN/UETA electronic-records disclosure + explicit
 *      checkbox + "I agree" button. A signing-status panel above the
 *      disclosure shows who has signed so far (you / the other borrower /
 *      Doorly), so the modal works as a status view even if the user is
 *      not the one who needs to sign next.
 *   1. Review — PDF iframe of the (already-partially-signed if the other
 *      borrower signed first, else pristine) borrower_psa, plus initials
 *      canvas for the requesting signer only.
 *   2. Identity + Signature — Typed legal name + canvas signature for
 *      the requesting signer only.
 *   3. Confirmation — Success view with hash digests + countersign status.
 *
 * Hash chain:
 *   The PDF bytes are fetched once via /api/documents/view/:id and
 *   hashed client-side (SubtleCrypto SHA-256). The digest is sent to the
 *   server as `clientHashBefore`; the server recomputes hash_before on
 *   the same bytes and rejects the request if they differ — proves the
 *   borrower signed the document they reviewed (defends against an admin
 *   regenerating the PSA between modal-open and signature-submit).
 *
 * Props:
 *   open      — modal visibility
 *   onClose   — close handler
 *   onSigned  — fires after successful submission (Dashboard refetches)
 *   apiUrl    — REACT_APP_API_URL
 *   token     — auth bearer
 */
const CONSENT_TEXT =
  'I agree to use electronic records and electronic signatures for this Purchase and Sale Agreement. ' +
  'I confirm that I can access and retain electronic documents in PDF format on the device I am using ' +
  'right now, that I understand my electronic signature is legally binding under the federal ESIGN Act ' +
  '(15 U.S.C. §§ 7001 et seq.) and the Texas Uniform Electronic Transactions Act, and that I may request ' +
  'a paper copy of any record or withdraw this consent by contacting Doorly at support@godoorly.com ' +
  'or 512-713-6016. I agree the document I review on this screen is the document I will sign.';

async function sha256(arrayBuffer) {
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)',
  // 10000: above Eva's launcher (DoorlyChat, 9999), which sat over the footer's Continue / Submit on a phone.
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
  padding: 16,
};
const sheetStyle = {
  background: '#fff', borderRadius: 16, width: '100%', maxWidth: 920,
  '--sm-max': '92vh',   // index.css .safe-modal-box display: 'flex', flexDirection: 'column', overflow: 'hidden',
  boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
};
const headerStyle = {
  padding: '18px 22px', borderBottom: '1px solid #e5e7eb',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  background: '#fff',
};
const bodyStyle = {
  padding: 22, overflowY: 'auto', flex: 1, background: '#fafafa',
};
const footerStyle = {
  padding: 16, borderTop: '1px solid #e5e7eb', background: '#fff',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
};
const primaryBtn = {
  padding: '11px 22px', borderRadius: 10, border: 'none',
  background: '#3a6ad9', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
};
const secondaryBtn = {
  padding: '11px 22px', borderRadius: 10, border: '1px solid #d1d5db',
  background: '#fff', color: '#374151', fontSize: 14, fontWeight: 700, cursor: 'pointer',
};
const labelStyle = { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, display: 'block' };
const inputStyle = {
  padding: '10px 12px', borderRadius: 10, border: '1px solid #d1d5db',
  width: '100%', fontSize: 14, fontFamily: 'inherit',
};
const stepDotStyle = (active, done) => ({
  width: 26, height: 26, borderRadius: '50%',
  background: done ? '#2d6a0a' : (active ? '#3a6ad9' : '#e5e7eb'),
  color: done || active ? '#fff' : '#9ca3af',
  fontSize: 12, fontWeight: 700,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
});

export default function PSASigningModal({ open, onClose, onSigned, apiUrl, token }) {
  const [phase, setPhase]                 = useState(0); // 0..3
  const [info, setInfo]                   = useState(null);
  const [loadingInfo, setLoadingInfo]     = useState(false);
  const [errorMsg, setErrorMsg]           = useState(null);

  // Phase 0
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentAt, setConsentAt]           = useState(null);

  // Phase 1 — initials (this signer only)
  const [borrowerInitials, setBorrowerInitials] = useState(null);

  // Phase 2 — identity + signature (this signer only)
  const [typedName,        setTypedName]        = useState('');
  const [borrowerSig,      setBorrowerSig]      = useState(null);

  // Document hash + bytes
  const [pdfBlobUrl,       setPdfBlobUrl]       = useState(null);
  const [hashBefore,       setHashBefore]       = useState(null);

  const [submitting,       setSubmitting]       = useState(false);
  const [result,           setResult]           = useState(null);

  // Track the latest blob URL we created so the cleanup effect can revoke
  // even the URL minted by the very last fetch (state may still be flushing).
  const blobUrlRef = useRef(null);
  // 🔑 INLINE ON A PHONE AND IN THE APP (fix/signing-scroll; hooks/useSigningScroll): no inner scroller inside the
  // popup's body — the consent text has no 220px box, and the PSA's pages are drawn inline (pdf.js, as the document
  // viewer does) instead of a 50vh iframe that scrolls on its own. One vertical scroll: the first touch gesture moves
  // the document. Desktop keeps the box and the iframe.
  const inlineDoc = useInlineDocument();
  useBodyScrollLock(!!open);   // the page behind stays put; only the popup's body scrolls
  const pdfBytesRef = useRef(null);
  const [pagesHost, setPagesHost] = useState(null);
  const [pagesDrawn, setPagesDrawn] = useState(0);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Reset state every time the modal is opened so a re-open after a
  // previous abort doesn't carry stale signatures/initials over.
  useEffect(() => {
    if (!open) return;
    setPhase(0);
    setConsentChecked(false);
    setConsentAt(null);
    setBorrowerInitials(null);
    setTypedName('');
    setBorrowerSig(null);
    setResult(null);
    setErrorMsg(null);
    setPagesDrawn(0);

    setLoadingInfo(true);
    axios.get(`${apiUrl}/api/option-period/bc-psa-info`, { headers })
      .then(async (r) => {
        setInfo(r.data);
        // This user has already signed — short-circuit to the status view.
        if (r.data?.already_signed) {
          setResult({
            alreadySigned: true,
            doorlySignedAt: r.data.doorly_signed_at,
            otherSigned:
              r.data.signer_role === 'primary'
                ? r.data.coborrower_signed_at
                : r.data.primary_signed_at,
          });
          setPhase(3);
          return;
        }
        // Fetch the PDF bytes via the scoped endpoint so co-borrowers
        // can read the primary's document. Hash chain matches the server
        // because /bc-psa-pdf uses the same source-PDF precedence as
        // utils/bcPsaCanvasSign.js.
        const pdfRes = await axios.get(
          `${apiUrl}/api/option-period/bc-psa-pdf`,
          { headers, responseType: 'arraybuffer' }
        );
        pdfBytesRef.current = pdfRes.data;
        const digest = await sha256(pdfRes.data);
        setHashBefore(digest);
        const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = blobUrl;
        setPdfBlobUrl(blobUrl);
      })
      .catch((err) => {
        setErrorMsg(err.response?.data?.error || err.message || 'Unable to load the PSA.');
      })
      .finally(() => setLoadingInfo(false));
  }, [open, apiUrl, headers]);

  // Revoke the blob URL on unmount/close to avoid leaking PDF bytes in memory.
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  // Inline: the PSA's pages, drawn to the body's width once the review step shows them.
  useEffect(() => {
    if (!open || !inlineDoc || phase !== 1 || !pagesHost || !pdfBlobUrl || !pdfBytesRef.current) return undefined;
    let live = true;
    pagesHost.innerHTML = '';
    setPagesDrawn(0);
    const width = Math.max(200, Math.floor(pagesHost.clientWidth));
    loadPdfJs().then(({ pdfjs, options }) => renderPdfPages({
      pdfjs, options, width,
      data: new Uint8Array(pdfBytesRef.current.slice(0)),   // a copy: pdf.js hands its buffer to the worker
      ratio: (n) => renderRatio(window.devicePixelRatio, n),
      makeCanvas: (i, dim) => {
        const c = document.createElement('canvas');
        c.width = dim.width; c.height = dim.height;
        c.style.width = `${dim.cssWidth}px`; c.style.height = `${dim.cssHeight}px`; c.style.maxWidth = '100%';
        c.style.display = 'block'; c.style.margin = '0 auto 10px'; c.style.background = '#fff';
        c.style.border = '1px solid #e5e7eb';
        c.setAttribute('data-testid', 'psa-page');
        c.setAttribute('aria-label', `Page ${i}`);
        if (live) pagesHost.appendChild(c);
        return c;
      },
      onPage: (i) => { if (live) setPagesDrawn(i); },
    })).catch(() => { if (live) setPagesDrawn(-1); });
    return () => { live = false; };
  }, [open, inlineDoc, phase, pagesHost, pdfBlobUrl]);

  if (!open) return null;

  const hasCo = !!info?.has_co_borrower;
  const signerRole = info?.signer_role || 'primary';

  // Which name/label belongs to the requesting signer vs the other party.
  const myName     = signerRole === 'coborrower' ? info?.coborrower_name : info?.primary_name;
  const otherName  = signerRole === 'coborrower' ? info?.primary_name    : info?.coborrower_name;
  const mySignedAt    = signerRole === 'coborrower' ? info?.coborrower_signed_at : info?.primary_signed_at;
  const otherSignedAt = signerRole === 'coborrower' ? info?.primary_signed_at    : info?.coborrower_signed_at;

  // ── Phase gating ─────────────────────────────────────────────────────
  const canAdvanceFromConsent = consentChecked;
  const canAdvanceFromReview  = !!borrowerInitials;
  const namesMatch = (val, expected) =>
    !!val && !!expected && val.trim().toLowerCase() === expected.trim().toLowerCase();
  const canSubmit =
    !!borrowerSig && !!typedName && namesMatch(typedName, myName) &&
    !!hashBefore && !submitting;

  const handleConsent = () => {
    setConsentAt(new Date().toISOString());
    setPhase(1);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch (_) { return ''; }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const payload = {
        signatureDataUrl: borrowerSig,
        initialsDataUrl:  borrowerInitials,
        typedName,
        consentText: CONSENT_TEXT,
        consentAcceptedAt: consentAt,
        clientHashBefore: hashBefore,
      };
      const res = await axios.post(
        `${apiUrl}/api/option-period/sign-bc-psa-canvas`,
        payload, { headers }
      );
      setResult(res.data);
      setPhase(3);
      if (onSigned) onSigned();
    } catch (err) {
      setErrorMsg(err.response?.data?.error || err.message || 'Signing failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── UI ───────────────────────────────────────────────────────────────
  return (
    <div className="safe-modal-overlay" style={overlayStyle} role="dialog" aria-modal="true">
      <div className="safe-modal-box safe-modal-shell" data-testid="psa-box" style={sheetStyle}>

        <div className="safe-modal-header" style={headerStyle}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
              Sign Your Purchase and Sale Agreement
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {info?.property_address || 'Loading…'}
            </div>
          </div>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 22, color: '#6b7280', cursor: 'pointer', lineHeight: 1 }}>
            ×
          </button>
        </div>

        {/* Step indicator */}
        {/* (fix/signing-scroll) on a phone and in the app only the CURRENT step is labelled (the others are their
            numbered dots): with all four labels the row was 4px wider than a 393px popup. */}
        <div className="safe-modal-header" style={{ padding: '12px 22px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #f3f4f6', background: '#fff' }}>
          {['Consent', 'Review & Initial', 'Sign', 'Done'].map((lbl, i) => (
            <React.Fragment key={lbl}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={stepDotStyle(phase === i, phase > i)}>{phase > i ? '✓' : i + 1}</div>
                {(!inlineDoc || phase === i) && <div style={{ fontSize: 12, fontWeight: 600, color: phase >= i ? '#111827' : '#9ca3af' }}>{lbl}</div>}
              </div>
              {i < 3 && <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />}
            </React.Fragment>
          ))}
        </div>

        <div className="safe-modal-body" data-testid="psa-body" style={bodyStyle}>
          {loadingInfo && (
            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
              Loading your Purchase and Sale Agreement…
            </div>
          )}

          {errorMsg && (
            <div style={{ padding: 14, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>
              {errorMsg}
            </div>
          )}

          {!loadingInfo && phase === 0 && (
            <div>
              {/* Signing status — who's signed so far. Renders for joint loans
                  and is informative for solo loans too (single-row). */}
              <div style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                padding: 14, marginBottom: 20,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Signing Status
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                    <span style={{ color: info?.primary_signed_at ? '#16a34a' : '#9ca3af', fontWeight: 700 }}>
                      {info?.primary_signed_at ? '✓' : '○'}
                    </span>
                    <span style={{ color: '#111827', fontWeight: 600 }}>{info?.primary_name || 'Primary borrower'}</span>
                    {info?.primary_signed_at && (
                      <span style={{ fontSize: 11, color: '#6b7280' }}>Signed {formatDate(info.primary_signed_at)}</span>
                    )}
                  </div>
                  {hasCo && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                      <span style={{ color: info?.coborrower_signed_at ? '#16a34a' : '#9ca3af', fontWeight: 700 }}>
                        {info?.coborrower_signed_at ? '✓' : '○'}
                      </span>
                      <span style={{ color: '#111827', fontWeight: 600 }}>{info?.coborrower_name || 'Co-borrower'}</span>
                      {info?.coborrower_signed_at && (
                        <span style={{ fontSize: 11, color: '#6b7280' }}>Signed {formatDate(info.coborrower_signed_at)}</span>
                      )}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                    <span style={{ color: info?.doorly_signed_at ? '#16a34a' : '#9ca3af', fontWeight: 700 }}>
                      {info?.doorly_signed_at ? '✓' : '○'}
                    </span>
                    <span style={{ color: '#111827', fontWeight: 600 }}>Go Doorly, LLC</span>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>
                      {info?.doorly_signed_at ? `Countersigned ${formatDate(info.doorly_signed_at)}` : 'Countersigns after all parties'}
                    </span>
                  </div>
                </div>
                {hasCo && otherSignedAt && !mySignedAt && (
                  <div style={{ marginTop: 12, padding: '8px 10px', background: '#f0fdf4', border: '1px solid #d4f99a', borderRadius: 8, fontSize: 12, color: '#2d6a0a' }}>
                    {otherName} has already signed — your signature completes the borrower side of the contract.
                  </div>
                )}
              </div>

              <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#111827' }}>Electronic Records Consent</h3>
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.7, margin: '0 0 14px' }}>
                Before you sign the Purchase and Sale Agreement, federal and Texas law require that
                you consent to using electronic records and electronic signatures. Please read the
                disclosure below and check the box if you agree.
              </p>
              <div style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                padding: 16, fontSize: 13, color: '#374151', lineHeight: 1.65, ...(inlineDoc ? {} : { maxHeight: 220, overflow: 'auto' }),
              }}>
                {CONSENT_TEXT}
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span style={{ fontSize: 14, color: '#111827', fontWeight: 600 }}>
                  I have read and agree to the Electronic Records Consent above.
                </span>
              </label>
            </div>
          )}

          {!loadingInfo && phase === 1 && (
            <div>
              <h3 style={{ margin: '0 0 8px', fontSize: 16, color: '#111827' }}>Review your Purchase and Sale Agreement</h3>
              <p style={{ color: '#374151', fontSize: 13, margin: '0 0 12px' }}>
                Scroll through the entire document. After reviewing, draw your initials below — they
                will appear on every page of the executed contract.
              </p>
              {pdfBlobUrl && inlineDoc && pagesDrawn >= 0 ? (
                <div data-testid="psa-pages-inline">
                  <div ref={setPagesHost} />
                  {pagesDrawn === 0 && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading PDF…</div>}
                </div>
              ) : pdfBlobUrl ? (
                <iframe
                  title="Purchase and Sale Agreement"
                  src={pdfBlobUrl}
                  style={{ width: '100%', height: '50vh', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff' }}
                />
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading PDF…</div>
              )}

              <div style={{ marginTop: 18 }}>
                <div style={labelStyle}>Your initials ({myName})</div>
                {borrowerInitials ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <img src={borrowerInitials} alt="initials"
                      style={{ height: 50, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 4 }} />
                    <button type="button" onClick={() => setBorrowerInitials(null)} style={secondaryBtn}>Redo</button>
                  </div>
                ) : (
                  <SignaturePad
                    width={260} height={100}
                    confirmLabel="Save Initials"
                    onSave={setBorrowerInitials}
                  />
                )}
              </div>
            </div>
          )}

          {!loadingInfo && phase === 2 && (
            <div>
              <h3 style={{ margin: '0 0 8px', fontSize: 16, color: '#111827' }}>Confirm identity & sign</h3>
              <p style={{ color: '#374151', fontSize: 13, margin: '0 0 14px' }}>
                Type your full legal name exactly as it appears on the contract, then draw your signature.
              </p>

              <div style={{ display: 'grid', gap: 16, gridTemplateColumns: '1fr' }}>
                <div>
                  <div style={labelStyle}>Your full legal name</div>
                  <input
                    type="text" value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder={myName || ''}
                    style={inputStyle}
                  />
                  {typedName && !namesMatch(typedName, myName) && (
                    <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>
                      Must match the contract name: <strong>{myName}</strong>
                    </div>
                  )}
                </div>
                <div>
                  <div style={labelStyle}>Your signature</div>
                  {borrowerSig ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <img src={borrowerSig} alt="signature"
                        style={{ height: 80, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 4 }} />
                      <button type="button" onClick={() => setBorrowerSig(null)} style={secondaryBtn}>Redo</button>
                    </div>
                  ) : (
                    <SignaturePad width={520} height={140} confirmLabel="Save Signature" onSave={setBorrowerSig} />
                  )}
                </div>
              </div>
            </div>
          )}

          {phase === 3 && (
            <div style={{ textAlign: 'center', padding: '24px 8px' }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%', background: '#f4fee8',
                color: '#2d6a0a', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 30, margin: '0 auto 16px', border: '1px solid #d4f99a',
              }}>✓</div>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, color: '#111827' }}>
                {result?.alreadySigned ? 'Already signed' : 'You\'re signed!'}
              </h3>
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.65, margin: '0 auto 14px', maxWidth: 540 }}>
                {(() => {
                  if (result?.alreadySigned) {
                    if (result.doorlySignedAt) return 'You have already signed. Doorly has countersigned — the contract is fully executed.';
                    if (hasCo && !result.otherSigned) return `You have already signed. Waiting on ${otherName || 'your co-borrower'} to sign before Doorly countersigns.`;
                    return 'You have already signed. Waiting on Doorly to countersign — you\'ll get an email when the contract is fully executed.';
                  }
                  if (result?.allBorrowersSigned && result?.countersignSent) {
                    return 'All borrowers have signed. Doorly will countersign shortly — you\'ll receive an email when the contract is fully executed.';
                  }
                  if (hasCo && !result?.allBorrowersSigned) {
                    return `Your signature is on file. Waiting for ${otherName || 'your co-borrower'} to sign before Doorly countersigns.`;
                  }
                  if (result?.countersignSent) {
                    return 'Your signature is on file. Doorly will countersign shortly — you\'ll receive an email when the contract is fully executed.';
                  }
                  return 'Your signature is on file. We\'re finalizing the countersign envelope — the Doorly team will be in touch shortly.';
                })()}
              </p>
              {result?.hashAfter && (
                <div style={{
                  fontSize: 11, color: '#6b7280', fontFamily: 'monospace', marginTop: 12, lineHeight: 1.6,
                  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, display: 'inline-block',
                }}>
                  Document fingerprint: {String(result.hashAfter).slice(0, 16)}…
                </div>
              )}
            </div>
          )}
        </div>

        <div className="safe-modal-footer" style={footerStyle}>
          <button type="button" onClick={onClose} style={secondaryBtn}>
            {phase === 3 ? 'Close' : 'Cancel'}
          </button>

          {phase === 0 && (
            <button type="button" onClick={handleConsent} disabled={!canAdvanceFromConsent}
              style={{ ...primaryBtn, opacity: canAdvanceFromConsent ? 1 : 0.5, cursor: canAdvanceFromConsent ? 'pointer' : 'not-allowed' }}>
              I Agree — Continue
            </button>
          )}
          {phase === 1 && (
            <button type="button" onClick={() => setPhase(2)} disabled={!canAdvanceFromReview}
              style={{ ...primaryBtn, opacity: canAdvanceFromReview ? 1 : 0.5, cursor: canAdvanceFromReview ? 'pointer' : 'not-allowed' }}>
              Continue to Sign
            </button>
          )}
          {phase === 2 && (
            <button type="button" onClick={handleSubmit} disabled={!canSubmit}
              style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
              {submitting ? 'Submitting…' : 'Submit Signature'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
